<template>
  <default>
    <div class="tw-w-full">
      <bread-crumb :breadcrumbs="breadcrumbs" />
      <v-card flat class="tw-mt-5 md:tw-mt-16">
        <div class="tw-px-4 md:tw-px-8">
          <card-title icon="mdi-car-windshield">Windscreen Claim Form</card-title>
          <p class="tw-px-2 md:tw-px-6">Complete the windscreen claim form below. Your details have been prefilled based on your account information.</p>
        </div>

        <div class="tw-px-4 md:tw-px-12 tw-mt-4 md:tw-mt-10">
          <v-form v-model="isValid" ref="windscreenForm">
            <!-- Important Notice -->
            <v-alert type="info" outlined class="tw-mb-6">
              <strong>Important Notice:</strong> This form must be completed fully and submitted within 30 days of the incident.
            </v-alert>

            <!-- Insurer Claim Number -->
            <div class="tw-mb-6">
              <v-text-field
                v-model="form.insurersClaimNumber"
                label="Insurers Claim Number (If known)"
                outlined
                dense
              ></v-text-field>
            </div>

            <!-- Company or Individual -->
            <div class="tw-mb-6">
              <h3 class="tw-text-lg tw-font-semibold tw-mb-4 c-navy-text">Policy Holder Type</h3>
              <v-radio-group
                v-model="form.companyOrIndividual"
                label="Policy Holder Type *"
                :rules="[required('Policy Holder Type')]"
                row
              >
                <v-radio label="Individual" value="Individual"></v-radio>
                <v-radio label="Company" value="Company"></v-radio>
              </v-radio-group>

              <!-- Company PIN field - only show when Company is selected -->
              <div v-if="form.companyOrIndividual === 'Company'" class="tw-mt-4">
                <v-text-field
                  v-model="form.companyPin"
                  label="Company PIN Number *"
                  placeholder="Enter company PIN number"
                  outlined
                  dense
                  :rules="form.companyOrIndividual === 'Company' ? [required('Company PIN')] : []"
                ></v-text-field>
              </div>
            </div>

            <!-- Insured Information Section -->
            <div class="tw-mb-6">
              <h3 class="tw-text-lg tw-font-semibold tw-mb-4 c-navy-text">Insured Information</h3>
              <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <v-text-field
                  v-model="form.insuredName"
                  label="Insured Name *"
                  :rules="[required('Insured Name')]"
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  v-model="form.mobileNumberMain"
                  label="Mobile Number (Main) *"
                  :rules="[required('Mobile Number'), mobileFormat()]"
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  v-model="form.mobileNumberAlternative"
                  label="Mobile Number (Alternative)"
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  v-model="form.emailAddress"
                  label="Email Address *"
                  :rules="[required('Email'), emailFormat()]"
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  v-model="form.idNumber"
                  label="ID Number *"
                  :rules="[required('ID Number'), nationalIdFormat()]"
                  outlined
                  dense
                ></v-text-field>

                <v-menu
                  v-model="dobMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.dateOfBirth"
                      label="Date of Birth *"
                      :rules="[required('Date of Birth')]"
                      prepend-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="form.dateOfBirth"
                    @input="dobMenu = false"
                  ></v-date-picker>
                </v-menu>

                <v-text-field
                  v-model="form.kraPinNo"
                  label="KRA PIN Number *"
                  :rules="[required('KRA PIN')]"
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  v-model="form.occupation"
                  label="Occupation *"
                  :rules="[required('Occupation')]"
                  outlined
                  dense
                ></v-text-field>
              </div>

              <!-- Address Information -->
              <h4 class="tw-text-md tw-font-semibold tw-mt-6 tw-mb-4 c-navy-text">Address Information</h4>
              <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <v-text-field
                  v-model="form.residentialArea"
                  label="Residential Area *"
                  :rules="[required('Residential Area')]"
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  v-model="form.streetRoad"
                  label="Street/Road *"
                  :rules="[required('Street/Road')]"
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  v-model="form.poBoxPostalCode"
                  label="P.O Box/Postal Code *"
                  :rules="[required('P.O Box/Postal Code')]"
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  v-model="form.townCity"
                  label="Town/City *"
                  :rules="[required('Town/City')]"
                  outlined
                  dense
                ></v-text-field>
              </div>
            </div>

            <!-- Intermediary Type -->
            <div class="tw-mb-6">
              <v-select
                v-model="form.intermediaryType"
                :items="intermediaryOptions"
                label="Intermediary Type *"
                :rules="[required('Intermediary Type')]"
                outlined
                dense
              ></v-select>
            </div>

            <!-- Vehicle Information Section -->
            <div class="tw-mb-6">
              <h3 class="tw-text-lg tw-font-semibold tw-mb-4 c-navy-text">Vehicle Information</h3>
              <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <v-text-field
                  v-model="form.registrationNumberOfVehicle"
                  label="Registration Number of Vehicle *"
                  :rules="[required('Registration Number')]"
                  outlined
                  dense
                  @input="capitalizeRegistrationNumber"
                  @blur="fetchPoliciesByRegNo"
                  style="text-transform: uppercase;"
                  :loading="loadingPoliciesByReg"
                ></v-text-field>

                <v-select
                  v-model="carMake"
                  :loading="carLoading"
                  :items="carMakeItems"
                  label="Vehicle Make *"
                  :rules="[required('Vehicle Make')]"
                  outlined
                  dense
                  @change="onMakeChange"
                ></v-select>

                <v-select
                  v-model="carModel"
                  :loading="modelLoading"
                  :items="carModelItems"
                  label="Vehicle Model *"
                  :rules="[required('Vehicle Model')]"
                  outlined
                  dense
                  :disabled="!carMake"
                  @change="updateMakeModel"
                ></v-select>

                <v-select
                  v-model="form.yearOfManufacture"
                  :items="yearsList"
                  label="Year of Manufacture *"
                  :rules="[required('Year of Manufacture')]"
                  outlined
                  dense
                ></v-select>

                <v-text-field
                  v-model="form.carryingCapacity"
                  label="Carrying Capacity *"
                  :rules="[required('Carrying Capacity')]"
                  outlined
                  dense
                ></v-text-field>
              </div>

              <!-- Fetched Policies Section -->
              <div v-if="fetchedPolicies.length > 0" class="tw-mt-6">
                <h4 class="tw-text-md tw-font-semibold tw-mb-4 c-navy-text">Available Policies for this Vehicle</h4>
                <v-alert type="info" outlined class="tw-mb-4">
                  <strong>{{ fetchedPolicies.length }} policy(ies) found</strong> for registration number {{ form.registrationNumberOfVehicle }}
                </v-alert>

                <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mb-4">
                  <v-card
                    v-for="(policy, index) in fetchedPolicies"
                    :key="index"
                    outlined
                    :class="{ 'tw-border-blue-500 tw-border-2': selectedPolicyFromReg === policy.policyNumber }"
                    class="tw-p-4 tw-cursor-pointer hover:tw-shadow-lg tw-transition-shadow"
                    @click="selectPolicyFromFetched(policy)"
                  >
                    <div class="tw-flex tw-justify-between tw-items-start tw-mb-2">
                      <div class="tw-font-semibold tw-text-sm">{{ policy.policyNumber }}</div>
                      <v-chip
                        :color="getPolicyStatusColor(policy.policyStatus)"
                        small
                        text-color="white"
                      >
                        {{ policy.policyStatus }}
                      </v-chip>
                    </div>

                    <div class="tw-text-sm tw-mb-1">
                      <strong>{{ policy.lastName }}, {{ policy.otherName }}</strong>
                    </div>
                    <div class="tw-text-xs tw-text-gray-600 tw-mb-2">
                      Product: {{ policy.product }}
                    </div>
                    <div class="tw-text-xs tw-text-gray-600 tw-mb-2">
                      Cover: {{ formatDate(policy.coverFrom) }} - {{ formatDate(policy.coverTo) }}
                    </div>
                    <div class="tw-text-xs tw-text-gray-600">
                      Premium: KES {{ policy.premium?.toLocaleString() }}
                    </div>

                    <v-btn
                      v-if="selectedPolicyFromReg === policy.policyNumber"
                      color="primary"
                      small
                      class="tw-mt-2"
                      disabled
                    >
                      <v-icon small class="tw-mr-1">mdi-check</v-icon>
                      Selected
                    </v-btn>
                  </v-card>
                </div>
              </div>

              <div v-else-if="searchedForPolicies && form.registrationNumberOfVehicle" class="tw-mt-6">
                <v-alert type="warning" outlined>
                  No policies found for registration number "{{ form.registrationNumberOfVehicle }}"
                </v-alert>
              </div>
            </div>

            <!-- Vehicle Use Section -->
            <div class="tw-mb-6">
              <h3 class="tw-text-lg tw-font-semibold tw-mb-4 c-navy-text">Vehicle Use</h3>
              <v-textarea
                v-model="form.stateExactPurposeOfVehicle"
                label="State EXACT PURPOSE of vehicle at time of accident *"
                :rules="[required('Purpose of vehicle')]"
                rows="3"
                outlined
                dense
              ></v-textarea>
            </div>

            <!-- Policy Information Section -->
            <div class="tw-mb-6">
              <h3 class="tw-text-lg tw-font-semibold tw-mb-4 c-navy-text">Policy Information</h3>
              <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <v-select
                  v-if="userPolicies.length > 0"
                  v-model="form.policyNumber"
                  :items="userPolicies"
                  item-text="displayText"
                  item-value="policyNo"
                  label="Select Your Policy *"
                  :rules="[required('Policy Number')]"
                  outlined
                  dense
                  :loading="loadingPolicies"
                  placeholder="Choose from your policies"
                ></v-select>
                <v-text-field
                  v-else
                  v-model="form.policyNumber"
                  label="Policy Number (Optional - No policies found)"
                  outlined
                  dense
                  :loading="loadingPolicies"
                  placeholder="Enter policy number if known"
                ></v-text-field>
              </div>
            </div>

            <!-- Driver Details Section -->
            <div class="tw-mb-6">
              <h3 class="tw-text-lg tw-font-semibold tw-mb-4 c-navy-text">Driver Details</h3>

              <!-- Is Driver Same as Insured -->
              <div class="tw-mb-4">
                <v-checkbox
                  v-model="isDriverSameAsInsured"
                  label="Driver is the same as the insured person"
                  color="primary"
                  @change="handleDriverSameAsInsuredChange"
                ></v-checkbox>
              </div>

              <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <v-text-field
                  v-model="form.driversName"
                  label="Driver's Name *"
                  :rules="[required('Driver Name')]"
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  v-model="form.driversMobileNumber"
                  label="Driver's Mobile Number *"
                  :rules="[required('Driver Mobile'), mobileFormat()]"
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  v-model="form.idNumberPassport"
                  label="ID Number/Passport *"
                  :rules="[required('Driver ID/Passport')]"
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  v-if="!isDriverSameAsInsured"
                  v-model="form.driversOccupation"
                  label="Driver's Occupation *"
                  :rules="[required('Driver Occupation')]"
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  v-model="form.driversEmailAddress"
                  label="Driver's Email Address *"
                  :rules="[required('Driver Email'), emailFormat()]"
                  outlined
                  dense
                ></v-text-field>
              </div>

              <!-- Driver Address -->
              <div v-if="!isDriverSameAsInsured">
                <h4 class="tw-text-md tw-font-semibold tw-mt-6 tw-mb-4 c-navy-text">Driver Address</h4>
                <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <v-text-field
                  v-model="form.residentialAddress"
                  label="Residential Address *"
                  :rules="[required('Driver Residential Address')]"
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  v-model="form.streetRoad1"
                  label="Street/Road *"
                  :rules="[required('Driver Street/Road')]"
                  outlined
                  dense
                ></v-text-field>

                <v-text-field
                  v-model="form.poBoxPostalCode1"
                  label="P.O Box/Postal Code *"
                  :rules="[required('Driver P.O Box/Postal Code')]"
                  outlined
                  dense
                ></v-text-field>
                </div>
              </div>
            </div>

            <!-- Accident Details Section -->
            <div class="tw-mb-6">
              <h3 class="tw-text-lg tw-font-semibold tw-mb-4 c-navy-text">Accident Details</h3>
              <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.dateOfTheAccident"
                      label="Date of the Accident *"
                      :rules="[required('Date of Accident')]"
                      prepend-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="form.dateOfTheAccident"
                    @input="dateMenu = false"
                  ></v-date-picker>
                </v-menu>

                <v-text-field
                  v-model="form.locationOfTheAccident"
                  label="Location of the Accident *"
                  :rules="[required('Location of Accident')]"
                  outlined
                  dense
                ></v-text-field>

                <v-menu
                  v-model="timeMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.timeOfTheAccidentAMPM"
                      label="Time of the Accident (AM/PM) *"
                      :rules="[required('Time of Accident')]"
                      prepend-icon="mdi-clock-outline"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-time-picker
                    v-model="selectedTime"
                    @input="timeMenu = false"
                    format="ampm"
                    :use-seconds="false"
                  ></v-time-picker>
                </v-menu>

                <v-select
                  v-model="form.visibility"
                  :items="visibilityOptions"
                  label="Visibility *"
                  :rules="[required('Visibility')]"
                  outlined
                  dense
                ></v-select>

                <v-text-field
                  v-model="form.estimateOfSpeedAtTimeOfAccident"
                  label="Estimate of Speed at Time of Accident (km/h) *"
                  :rules="[required('Speed at time of accident')]"
                  type="number"
                  outlined
                  dense
                ></v-text-field>

                <v-select
                  v-model="form.weatherConditions"
                  :items="weatherConditionsOptions"
                  label="Weather Conditions *"
                  :rules="[required('Weather Conditions')]"
                  outlined
                  dense
                ></v-select>

                <v-select
                  v-model="form.typeOfRoadSurface"
                  :items="roadSurfaceOptions"
                  label="Type of Road Surface *"
                  :rules="[required('Road Surface Type')]"
                  outlined
                  dense
                ></v-select>
              </div>

              <v-textarea
                v-model="form.statementByDriver"
                label="Statement by Driver (Describe what happened) *"
                :rules="[required('Driver Statement')]"
                rows="4"
                outlined
                dense
                class="tw-mt-4"
              ></v-textarea>
            </div>

            <!-- Replacement or Refund Section -->
            <div class="tw-mb-6">
              <h3 class="tw-text-lg tw-font-semibold tw-mb-4 c-navy-text">Claim Type</h3>
              <v-radio-group
                v-model="form.replacementOrRefund"
                label="Replacement or Refund *"
                :rules="[required('Claim Type')]"
                row
              >
                <v-radio label="Replacement Letter" value="Replacement Letter"></v-radio>
                <v-radio label="Refund" value="Refund"></v-radio>
              </v-radio-group>
            </div>

            <!-- Date of Completion -->
            <div class="tw-mb-6">
              <v-menu
                v-model="completionDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.dateOfCompletionOfClaimForm"
                    label="Date of Completion of Claim Form *"
                    :rules="[required('Date of Completion')]"
                    prepend-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.dateOfCompletionOfClaimForm"
                  @input="completionDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </div>

            <!-- Attachments Section -->
            <div class="tw-mb-6">
              <h3 class="tw-text-lg tw-font-semibold tw-mb-4 c-navy-text">Attachments</h3>
              <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <!-- Driver Signature -->
                <v-card outlined class="tw-p-4">
                  <h4 class="tw-font-medium tw-mb-3">Driver Signature</h4>
                  <express-upload
                    :uid="authenticatedUser['custom:kra-pin']"
                    :phoneNumber="authenticatedUser.phone_number"
                    :fullName="authenticatedUser.name"
                    :email="authenticatedUser.email"
                    :companyPin="form.companyPin"
                    name="Upload Signature"
                    :bucket="bucket"
                    accept="image/*"
                  />
                </v-card>

                <!-- Windscreen Photo BEFORE Replacement -->
                <v-card outlined class="tw-p-4">
                  <h4 class="tw-font-medium tw-mb-3">Windscreen Photo BEFORE Replacement</h4>
                  <express-upload
                    :uid="authenticatedUser['custom:kra-pin']"
                    :phoneNumber="authenticatedUser.phone_number"
                    :fullName="authenticatedUser.name"
                    :email="authenticatedUser.email"
                    :companyPin="form.companyPin"
                    name="Windscreen Photo BEFORE Replacement"
                    :bucket="bucket"
                    accept="image/*"
                  />
                </v-card>

                <!-- Whole Car Section Photos BEFORE Replacement -->
                <v-card outlined class="tw-p-4 tw-col-span-1 md:tw-col-span-2">
                  <h4 class="tw-font-medium tw-mb-3">Whole Car Section Photos BEFORE Replacement</h4>
                  <express-upload
                    :uid="authenticatedUser['custom:kra-pin']"
                    :phoneNumber="authenticatedUser.phone_number"
                    :fullName="authenticatedUser.name"
                    :email="authenticatedUser.email"
                    :companyPin="form.companyPin"
                    name="Whole Car Section Photos BEFORE Replacement"
                    :bucket="bucket"
                    accept="image/*"
                  />
                </v-card>

                <!-- Optional Attachments - Only show when Refund is selected -->
                <v-expansion-panels v-if="form.replacementOrRefund === 'Refund'" class="tw-col-span-2">
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <strong>Required Attachments (After Replacement)</strong>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mt-4">
                        <v-card outlined class="tw-p-4">
                          <h4 class="tw-font-medium tw-mb-3">Windscreen Photo AFTER Replacement</h4>
                          <express-upload
                            :uid="authenticatedUser['custom:kra-pin']"
                            :phoneNumber="authenticatedUser.phone_number"
                            :fullName="authenticatedUser.name"
                            :email="authenticatedUser.email"
                            :companyPin="form.companyPin"
                            name="Windscreen Photo AFTER Replacement"
                            :bucket="bucket"
                            accept="image/*"
                          />
                        </v-card>

                        <v-card outlined class="tw-p-4">
                          <h4 class="tw-font-medium tw-mb-3">Original Receipts</h4>
                          <express-upload
                            :uid="authenticatedUser['custom:kra-pin']"
                            :phoneNumber="authenticatedUser.phone_number"
                            :fullName="authenticatedUser.name"
                            :email="authenticatedUser.email"
                            :companyPin="form.companyPin"
                            name="Original Receipts (repair or replacement)"
                            :bucket="bucket"
                          />
                        </v-card>

                        <v-card outlined class="tw-p-4 tw-col-span-1 md:tw-col-span-2">
                          <h4 class="tw-font-medium tw-mb-3">Whole Car Section Photos AFTER Replacement</h4>
                          <express-upload
                            :uid="authenticatedUser['custom:kra-pin']"
                            :phoneNumber="authenticatedUser.phone_number"
                            :fullName="authenticatedUser.name"
                            :email="authenticatedUser.email"
                            :companyPin="form.companyPin"
                            name="Whole Car Section Photos AFTER Replacement"
                            :bucket="bucket"
                            accept="image/*"
                          />
                        </v-card>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
            </div>

            <!-- Declaration Section -->
            <div class="tw-mb-6">
              <h3 class="tw-text-lg tw-font-semibold tw-mb-4 c-navy-text">Declaration</h3>
              <v-checkbox
                v-model="form.declaration1"
                :rules="[v => !!v || 'Declaration is required']"
                color="primary"
                class="tw-mb-4"
              >
                <template v-slot:label>
                  <div class="tw-text-sm">
                    I/We declare that to my/our knowledge that the answers and particulars given in this Claim Form are true and complete and that I/we have not withheld any material information. I/We have also read and understood that this Claim Form and Declaration shall be the basis of the Contract between me/us and PACIS Insurance Company Limited
                  </div>
                </template>
              </v-checkbox>

              <v-checkbox
                v-model="form.dataPrivacy1"
                :rules="[v => !!v || 'Data Privacy Consent is required']"
                color="primary"
              >
                <template v-slot:label>
                  <div class="tw-text-sm">
                    I/ We willingly provide my/ our personal information in this Claim Form and consent its use as prescribed in the Pacis Insurance Data Protection Policy (The policy is available on our website www.pacisinsurance.com) and in accordance with Data Protection Act, 2019
                  </div>
                </template>
              </v-checkbox>
            </div>

            <!-- Submit Button -->
            <v-card-actions class="tw-flex tw-justify-center tw-mt-8 tw-pb-8">
              <div class="tw-w-full md:tw-w-96">
                <v-btn
                  block
                  large
                  color="secondary"
                  class="tw-mb-4"
                  @click="submitForm"
                  :disabled="!isValid"
                  :loading="submitting"
                >
                  {{ isValid && form.declaration1 && form.dataPrivacy1 ? 'Submit Windscreen Claim' : 'Please fill all required fields and accept declarations' }}
                </v-btn>

                <v-btn
                  block
                  text
                  color="grey"
                  @click="goBack"
                >
                  Cancel
                </v-btn>
              </div>
            </v-card-actions>
          </v-form>
        </div>
      </v-card>
    </div>
  </default>
</template>

<script>
import axios from 'axios';
import CardTitle from '@/components/shared/CardTitle';
import { mapGetters } from 'vuex';
import Default from '@/components/layout/Default';
import BreadCrumb from '@/components/dashboard/BreadCrumb';
import ExpressUpload from '@/components/shared/ExpressUpload';
import validations from '@/utils/validations';

export default {
  name: 'WindscreenClaimForm',
  components: {
    ExpressUpload,
    BreadCrumb,
    Default,
    CardTitle,
  },
  data() {
    return {
      isValid: false,
      submitting: false,
      dateMenu: false,
      dobMenu: false,
      completionDateMenu: false,
      timeMenu: false,
      selectedTime: null,
      isDriverSameAsInsured: true,
      kraPin: 'custom:kra-pin',
      policyNo: null,
      userPolicies: [],
      loadingPolicies: false,
      fetchedPolicies: [],
      loadingPoliciesByReg: false,
      searchedForPolicies: false,
      selectedPolicyFromReg: null,
      // Make and Model data
      carMakes: [],
      carMake: null,
      carMakeItems: [],
      carLoading: false,
      carModels: [],
      carModel: null,
      carModelItems: [],
      modelLoading: false,
      yearsList: [],
      breadcrumbs: [
        {
          text: 'Make A Claim',
          disabled: false,
          to: { name: 'Claims' },
        },
        {
          text: 'Windscreen Claim Form',
          disabled: true,
        },
      ],
      bucket: 'gi-claims',
      intermediaryOptions: [
        'Direct',
        'Agent',
        'Broker',
        'Bancassurance',
        'Waumini',
      ],
      roadSurfaceOptions: [
        'Dry',
        'Wet',
        'Muddy',
        'Sandy',
        'Gravel',
      ],
      weatherConditionsOptions: [
        'Clear/Sunny',
        'Cloudy',
        'Rainy',
        'Heavy Rain',
        'Drizzle',
        'Foggy',
        'Misty',
        'Windy',
        'Stormy',
        'Hail',
        'Other',
      ],
      visibilityOptions: [
        'Good',
        'Fair',
        'Poor',
        'Very Poor',
        'Clear',
        'Moderate',
        'Limited',
        'Restricted',
        'Excellent',
      ],
      form: {
        // Basic Information
        insurersClaimNumber: '',
        companyOrIndividual: 'Individual',
        companyPin: '',

        // Insured Information
        insuredName: '',
        mobileNumberMain: '',
        mobileNumberAlternative: '',
        emailAddress: '',
        idNumber: '',
        dateOfBirth: '',
        kraPinNo: '',
        occupation: '',

        // Address Information
        residentialArea: '',
        streetRoad: '',
        poBoxPostalCode: '',
        townCity: '',

        // Intermediary
        intermediaryType: 'Direct',

        // Vehicle Information
        registrationNumberOfVehicle: '',
        makeModel: '',
        yearOfManufacture: '',
        carryingCapacity: '',

        // Vehicle Use
        stateExactPurposeOfVehicle: '',

        // Policy Information
        policyNumber: '',

        // Driver Details
        driversName: '',
        driversMobileNumber: '',
        idNumberPassport: '',
        driversOccupation: '',
        driversEmailAddress: '',
        kraPinNo1: '',

        // Driver Address
        residentialAddress: '',
        streetRoad1: '',
        poBoxPostalCode1: '',

        // Accident Details
        dateOfTheAccident: '',
        locationOfTheAccident: '',
        timeOfTheAccidentAMPM: '',
        visibility: '',
        estimateOfSpeedAtTimeOfAccident: '',
        weatherConditions: '',
        typeOfRoadSurface: 'Dry',
        statementByDriver: '',

        // Claim Type
        replacementOrRefund: 'Replacement Letter',

        // Completion Date
        dateOfCompletionOfClaimForm: new Date().toISOString().slice(0, 10),

        // Declarations
        declaration1: false,
        dataPrivacy1: false,
      },
      ...validations,
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser']),
    ...mapGetters('client', ['client']),
  },
  watch: {
    'form.insuredName': function syncInsuredNameToDriver(newVal) {
      if (this.isDriverSameAsInsured) {
        this.form.driversName = newVal;
      }
    },
    'form.emailAddress': function syncEmailToDriver(newVal) {
      if (this.isDriverSameAsInsured) {
        this.form.driversEmailAddress = newVal;
      }
    },
    'form.mobileNumberMain': function syncMobileToDriver(newVal) {
      if (this.isDriverSameAsInsured) {
        this.form.driversMobileNumber = newVal;
      }
    },
    'form.kraPinNo': function syncKraPinToDriver(newVal) {
      if (this.isDriverSameAsInsured) {
        this.form.kraPinNo1 = newVal;
      }
    },
    'form.idNumber': function syncIdNumberToDriver(newVal) {
      if (this.isDriverSameAsInsured) {
        this.form.idNumberPassport = newVal;
      }
    },
    'form.occupation': function syncOccupationToDriver(newVal) {
      if (this.isDriverSameAsInsured) {
        this.form.driversOccupation = newVal;
      }
    },
    selectedTime(newTime) {
      if (newTime) {
        // Convert 24-hour format to 12-hour format with AM/PM
        const [hours, minutes] = newTime.split(':');
        const hour12 = hours % 12 || 12;
        const ampm = hours < 12 ? 'AM' : 'PM';
        this.form.timeOfTheAccidentAMPM = `${hour12}:${minutes} ${ampm}`;
      }
    },
  },
  async mounted() {
    this.prefillUserDetails();

    // Generate years list for dropdown
    this.generateYearsList();

    // Fetch car makes for the dropdowns
    this.getCarMakes();

    // Wait for client to be loaded before fetching policies
    if (this.client) {
      await this.fetchPolicyData();
    } else {
      // Watch for client to be loaded
      this.$watch('client', async (newVal) => {
        if (newVal) {
          await this.fetchPolicyData();
        }
      }, { immediate: true });
    }

    // Add global error handler to prevent page crashes
    window.addEventListener('unhandledrejection', this.handleUnhandledError);
  },
  beforeDestroy() {
    // Clean up event listener
    window.removeEventListener('unhandledrejection', this.handleUnhandledError);
  },
  methods: {
    prefillUserDetails() {
      if (this.authenticatedUser) {
        // Prefill insured information
        this.form.insuredName = this.authenticatedUser.name || '';
        this.form.emailAddress = this.authenticatedUser.email || '';
        this.form.mobileNumberMain = this.authenticatedUser.phone_number || '';
        this.form.kraPinNo = this.authenticatedUser[this.kraPin] || '';
        this.form.idNumber = this.authenticatedUser['custom:id_number'] || '';

        // Prefill driver details if driver is same as insured (default)
        if (this.isDriverSameAsInsured) {
          this.copyInsuredToDriver();
        }
      }
    },

    handleDriverSameAsInsuredChange() {
      if (this.isDriverSameAsInsured) {
        this.copyInsuredToDriver();
      } else {
        this.clearDriverDetails();
      }
    },

    copyInsuredToDriver() {
      this.form.driversName = this.form.insuredName;
      this.form.driversEmailAddress = this.form.emailAddress;
      this.form.driversMobileNumber = this.form.mobileNumberMain;
      this.form.kraPinNo1 = this.form.kraPinNo;
      this.form.idNumberPassport = this.form.idNumber;
      this.form.driversOccupation = this.form.occupation;
    },

    clearDriverDetails() {
      this.form.driversName = '';
      this.form.driversEmailAddress = '';
      this.form.driversMobileNumber = '';
      this.form.kraPinNo1 = '';
      this.form.idNumberPassport = '';
      this.form.driversOccupation = '';
      this.form.residentialAddress = '';
      this.form.streetRoad1 = '';
      this.form.poBoxPostalCode1 = '';
    },

    async fetchPolicyData() {
      // Get policy number from route params or query for backward compatibility
      this.policyNo = this.$route.params.policyNo || this.$route.query.policyNo;

      if (this.policyNo) {
        this.form.policyNumber = this.policyNo;
      }

      await this.fetchUserPolicies();
    },

    async fetchPoliciesByRegNo() {
      const regNo = this.form.registrationNumberOfVehicle?.trim();
      if (!regNo || regNo.length < 3) {
        this.fetchedPolicies = [];
        this.searchedForPolicies = false;
        return;
      }

      this.loadingPoliciesByReg = true;
      this.searchedForPolicies = false;

      try {
        const response = await axios.get(`/customer360/gis/policies/policyoverview/regNo?regNo=${regNo}`);

        if (response.data && response.data.object && Array.isArray(response.data.object)) {
          this.fetchedPolicies = response.data.object.filter((policy) => policy.currentStatus === 'A');

          if (this.fetchedPolicies.length > 0) {
            this.$toast.success(`Found ${this.fetchedPolicies.length} active policy(ies) for ${regNo}`, 'Policies Found');

            // Auto-select the first active policy if there's only one
            if (this.fetchedPolicies.length === 1) {
              this.selectPolicyFromFetched(this.fetchedPolicies[0]);
            }
          } else {
            this.$toast.info(`No active policies found for registration number ${regNo}`, 'No Active Policies');
          }
        } else {
          this.fetchedPolicies = [];
        }
      } catch (error) {
        this.fetchedPolicies = [];

        if (error.response?.status === 404) {
          this.$toast.info(`No policies found for registration number ${regNo}`, 'No Policies Found');
        } else {
          this.$toast.error('Error fetching policies. Please try again.', 'Fetch Error');
        }
      } finally {
        this.loadingPoliciesByReg = false;
        this.searchedForPolicies = true;
      }
    },

    selectPolicyFromFetched(policy) {
      this.selectedPolicyFromReg = policy.policyNumber;
      this.form.policyNumber = policy.policyNumber;

      // Optionally prefill customer information if it matches
      if (policy.emailAddress && !this.form.emailAddress) {
        this.form.emailAddress = policy.emailAddress;
      }
      if (policy.phoneNumber && !this.form.mobileNumberMain) {
        this.form.mobileNumberMain = policy.phoneNumber;
      }
      if (policy.lastName && policy.otherName && !this.form.insuredName) {
        this.form.insuredName = `${policy.otherName} ${policy.lastName}`;
      }
      if (policy.kraPin && !this.form.kraPinNo) {
        this.form.kraPinNo = policy.kraPin;
      }
      if (policy.idNumber && !this.form.idNumber) {
        this.form.idNumber = policy.idNumber;
      }

      this.$toast.success(`Selected policy ${policy.policyNumber}`, 'Policy Selected');
    },

    getPolicyStatusColor(status) {
      switch (status) {
        case 'RN': return 'green';
        case 'EN': return 'orange';
        case 'NB': return 'blue';
        default: return 'grey';
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString();
    },

    async fetchUserPolicies() {
      if (!this.authenticatedUser || !this.client) {
        return;
      }

      this.loadingPolicies = true;

      try {
        const response = await axios.get(`/customer/gis/policies/client/productGroup/70?clientCode=${this.client.clientCode}&pageNumber=1&limit=50`);

        if (response.data && response.data.object && response.data.object.pageItems) {
          this.userPolicies = response.data.object.pageItems.map((policy) => ({
            policyNo: policy.policyNo,
            displayText: `${policy.policyNo} - ${policy.risks[0].model}, ${policy.risks[0].regno.toUpperCase()}`,
          }));
        } else {
          this.$toast.info('No policies found for your account. You can still proceed with the claim form.', 'No Policies');
          this.userPolicies = [];
        }
      } catch (error) {
        this.$toast.error('Could not load your policies. Please refresh and try again.', 'Policy Error');
        this.userPolicies = [];
      } finally {
        this.loadingPolicies = false;
      }
    },

    // Generate years list for dropdown
    generateYearsList() {
      const currentYear = new Date().getFullYear();
      const startYear = 1980; // Start from 1980 for reasonable vehicle age range
      const years = [];

      for (let year = currentYear; year >= startYear; year -= 1) {
        years.push(year.toString());
      }

      this.yearsList = years;
    },

    // Make and Model methods
    getCarMakes() {
      this.carLoading = true;
      axios.get('/customer/gis/vehicles/make/retrieveAll').then((response) => {
        this.carLoading = false;

        if (response.data && response.data.object) {
          this.carMakes = response.data.object;
          // Populate the items array for the dropdown
          this.carMakeItems = this.carMakes.map((make) => make.makeDetails);
        } else {
          this.carMakes = [];
          this.carMakeItems = [];
        }
      }).catch(() => {
        this.$toast.error('Failed to load vehicle makes. Using fallback list.', 'Error');
        this.carLoading = false;

        // Fallback list of common car makes
        this.carMakeItems = [
          'Toyota', 'Nissan', 'Honda', 'Mazda', 'Subaru', 'Mitsubishi',
          'Volkswagen', 'Mercedes-Benz', 'BMW', 'Audi', 'Hyundai', 'Kia',
          'Peugeot', 'Ford', 'Chevrolet', 'Land Rover', 'Jeep', 'Suzuki',
          'Isuzu', 'Daihatsu', 'Other',
        ];
        this.carMakes = this.carMakeItems.map((make) => ({ makeDetails: make }));
      });
    },

    async getCarModels(id) {
      this.modelLoading = true;
      await axios.get(`/customer/gis/vehicles/${id}/models`).then((response) => {
        this.modelLoading = false;

        if (response.data && response.data.object) {
          this.carModels = response.data.object;
          // Populate the models dropdown
          this.carModelItems = this.carModels.map((model) => model.modelname);
        } else {
          this.carModels = [];
          this.carModelItems = [];
        }
      }).catch(() => {
        this.$toast.error('Failed to load vehicle models. Please try again.', 'Error');
        this.modelLoading = false;
        this.carModels = [];
        this.carModelItems = [];
      });
    },

    onMakeChange() {
      // Clear the selected model when make changes
      this.carModel = null;
      this.carModelItems = [];

      // Find the selected make object and get its models
      const selectedMakeObj = this.carMakes.find((make) => make.makeDetails === this.carMake);
      if (selectedMakeObj) {
        this.getCarModels(selectedMakeObj.vmkcode);
      }

      this.updateMakeModel();
    },

    capitalizeRegistrationNumber() {
      if (this.form.registrationNumberOfVehicle) {
        this.form.registrationNumberOfVehicle = this.form.registrationNumberOfVehicle.toUpperCase();
      }
    },

    updateMakeModel() {
      // Update the form field with the selected make and model
      if (this.carMake && this.carModel) {
        this.form.makeModel = `${this.carMake} ${this.carModel}`;
      } else if (this.carMake) {
        this.form.makeModel = this.carMake;
      } else {
        this.form.makeModel = '';
      }
    },

    async submitForm() {
      if (!this.isValid) return;

      this.submitting = true;

      try {
        // eslint-disable-next-line no-unused-vars
        const claimData = {
          FormGuid: '2ea43beb-9fca-4f49-9380-086bbd509a31',
          IsPublic: true,
          ...this.form,
          submittedAt: new Date().toISOString(),
          userId: this.authenticatedUser[this.kraPin],
          status: 'submitted',
          notificationEmails: ['claims@paciskenya.com', 'eopiyo@paciskenya.com'],
        };

        // In a real implementation, you would submit this to your backend API

        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 2000));

        this.$toast.success('Windscreen claim submitted successfully! You will receive a confirmation email shortly.', 'Success');

        // Redirect back to claims page
        this.$router.push({ name: 'Claims' });
      } catch (error) {
        this.$toast.error('Error submitting claim. Please try again.', 'Error');
      } finally {
        this.submitting = false;
      }
    },

    goBack() {
      this.$router.push({ name: 'Claims' });
    },

    handleUnhandledError(event) {
      // Prevent the error from crashing the page
      event.preventDefault();

      // User-friendly error message
      if (event.reason && event.reason.message && event.reason.message.includes('504')) {
        this.$toast.error('Connection timeout. Your form data is preserved. Please try the upload again.', 'Upload Error');
      } else if (event.reason && event.reason.message && event.reason.message.includes('Request failed')) {
        this.$toast.error('Upload failed. Your form data is preserved. Please try again.', 'Upload Error');
      } else {
        this.$toast.error('An error occurred. Your form data is preserved. Please refresh if issues persist.', 'Error');
      }
    },
  },
};
</script>

<style scoped>
.c-navy-text {
  color: #1a365d;
}
</style>
